import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { isPlexDirectory } from '@tunarr/types/plex';
import { find, isNil, map } from 'lodash-es';
import { useCallback, useEffect } from 'react';
import { Plex } from '../../../helpers/constants.ts';
import {
  usePlexLibraries,
  usePlexPlaylists,
} from '../../../hooks/plex/usePlex.ts';
import { Route } from '../../../routes/channels_/$channelId/programming/add.tsx';
import useStore from '../../../store/index.ts';
import {
  addKnownMediaForPlexServer,
  setProgrammingListLibrary,
} from '../../../store/programmingSelector/actions.ts';
import { useKnownMedia } from '../../../store/programmingSelector/selectors.ts';

type Props = {
  initialLibraryId?: string;
};

export const PlexLibrarySelector = ({ initialLibraryId }: Props) => {
  const selectedServer = useStore((s) => s.currentMediaSource);
  const selectedLibrary = useStore((s) => s.currentMediaSourceView);
  const navigate = Route.useNavigate();

  const { data: plexLibraryChildren } = usePlexLibraries(
    selectedServer?.id ?? '',
    selectedServer?.type === Plex,
  );

  const { data: plexPlaylists, isLoading: plexPlaylistsLoading } =
    usePlexPlaylists(selectedServer?.id ?? '', selectedServer?.type === Plex);

  const knownMedia = useKnownMedia();

  const onLibraryChange = useCallback(
    (libraryUuid: string) => {
      if (!selectedServer) {
        return;
      }

      if (libraryUuid === 'playlists' && plexPlaylists) {
        setProgrammingListLibrary({
          type: Plex,
          view: { type: 'playlists', playlists: plexPlaylists },
        });
        return;
      }

      const library = knownMedia.getPlexMedia(selectedServer.id, libraryUuid);

      if (library && isPlexDirectory(library)) {
        setProgrammingListLibrary({
          type: Plex,
          view: { type: 'library', library },
        });
        navigate({
          search: {
            mediaSourceId: selectedServer.id,
            libraryId: library.uuid,
          },
        }).catch(console.error);
      }
    },
    [knownMedia, navigate, plexPlaylists, selectedServer],
  );

  useEffect(() => {
    if (selectedServer && plexLibraryChildren) {
      addKnownMediaForPlexServer(selectedServer.id, [
        ...plexLibraryChildren.Directory,
      ]);

      if (
        plexLibraryChildren.size > 0 &&
        (!selectedLibrary || selectedLibrary.type !== Plex)
      ) {
        const initialLibrary = find(
          plexLibraryChildren?.Directory,
          ({ uuid }) => uuid === initialLibraryId,
        );
        setProgrammingListLibrary({
          type: Plex,
          view: {
            type: 'library',
            library: initialLibrary ?? plexLibraryChildren.Directory[0],
          },
        });
        navigate({
          search: {
            libraryId: plexLibraryChildren.Directory[0].uuid,
            mediaSourceId: selectedServer.id,
          },
        }).catch(console.error);
      }
    }
  }, [
    initialLibraryId,
    navigate,
    plexLibraryChildren,
    selectedLibrary,
    selectedServer,
  ]);

  const selectedPlexLibrary =
    selectedLibrary?.type === Plex ? selectedLibrary.view : undefined;

  const hasLibraries =
    !isNil(plexLibraryChildren) &&
    plexLibraryChildren.size > 0 &&
    selectedPlexLibrary;

  const libraryMenuItems = map(plexLibraryChildren?.Directory, (dir) => (
    <MenuItem key={dir.key} value={dir.uuid}>
      {dir.title}
    </MenuItem>
  ));

  const playlistMenuItem = (
    <MenuItem key="playlists" value="playlists" disabled={plexPlaylistsLoading}>
      Playlists
    </MenuItem>
  );

  return (
    hasLibraries && (
      <FormControl size="small" sx={{ minWidth: { sm: 200 } }}>
        <InputLabel>Library</InputLabel>
        <Select
          label="Library"
          value={
            selectedPlexLibrary.type === 'library'
              ? selectedPlexLibrary.library.uuid
              : 'playlists'
          }
          onChange={(e) => onLibraryChange(e.target.value)}
        >
          {libraryMenuItems}
          {playlistMenuItem}
        </Select>
      </FormControl>
    )
  );
};
